import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { formatAddress } from '../lib/utils';
import { RankBadge } from '../components/RankBadge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { 
    BarChart3,
    Trophy,
    Users,
    Coins,
    TrendingUp,
    TrendingDown,
    Calendar,
    Target,
    Clock,
    Award,
    Zap,
    Medal
} from 'lucide-react';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#FFD700', '#00FF66', '#9B6FE8', '#FF6B6B', '#4ECDC4'];

// Stats Card Component
function StatCard({ title, value, subValue, icon: Icon, color, trend }) {
    return (
        <Card className="bg-white/[0.02] border-white/5">
            <CardContent className="p-4">
                <div className="flex items-start justify-between">
                    <div>
                        <p className="text-white/50 text-sm mb-1">{title}</p>
                        <div className="flex items-baseline gap-2">
                            <span className={`text-2xl font-bold ${color || 'text-white'}`}>{value}</span>
                            {subValue && <span className="text-white/40 text-sm">{subValue}</span>}
                        </div>
                        {trend !== undefined && (
                            <div className={`flex items-center gap-1 mt-1 text-xs ${trend >= 0 ? 'text-[#00FF66]' : 'text-red-400'}`}>
                                {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                                {Math.abs(trend)}% vs last period
                            </div>
                        )}
                    </div>
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color ? `bg-${color}/10` : 'bg-white/5'}`}>
                        <Icon className={`w-5 h-5 ${color || 'text-white/60'}`} />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

// Recent Winners Table
function RecentWinners({ winners }) {
    if (!winners || winners.length === 0) {
        return (
            <div className="text-center py-8 text-white/40">
                No winners data yet
            </div>
        );
    }
    
    return (
        <div className="space-y-2">
            {winners.map((winner, idx) => (
                <div 
                    key={idx}
                    className="flex items-center justify-between p-3 rounded-lg bg-white/[0.03] hover:bg-white/[0.05] transition-colors"
                >
                    <div className="flex items-center gap-3">
                        <RankBadge rank={winner.place} size="sm" />
                        <div>
                            <div className="text-white font-mono text-sm">
                                {formatAddress(winner.address)}
                            </div>
                            <div className="text-white/40 text-xs">{winner.tournament_name}</div>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-[#00FF66] font-bold">{winner.prize} ETH</div>
                        <div className="text-white/40 text-xs">{winner.date}</div>
                    </div>
                </div>
            ))}
        </div>
    );
}

export function TournamentAnalyticsPage() {
    const { address, isConnected } = useWeb3();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalTournaments: 0,
        activeTournaments: 0,
        totalParticipants: 0,
        totalPrizePool: 0,
        avgParticipants: 0,
        avgPrizePool: 0
    });
    const [tournaments, setTournaments] = useState([]);
    const [participationData, setParticipationData] = useState([]);
    const [prizeDistribution, setPrizeDistribution] = useState([]);
    const [recentWinners, setRecentWinners] = useState([]);
    const [tournamentTypes, setTournamentTypes] = useState([]);
    
    useEffect(() => {
        fetchAnalytics();
    }, []);
    
    const fetchAnalytics = async () => {
        setLoading(true);
        try {
            // Fetch tournaments
            const tournamentsRes = await fetch(`${API_URL}/api/tournaments?limit=100`);
            if (tournamentsRes.ok) {
                const tournamentsData = await tournamentsRes.json();
                setTournaments(tournamentsData);
                
                // Calculate stats
                const total = tournamentsData.length;
                const active = tournamentsData.filter(t => t.status === 'active').length;
                const upcoming = tournamentsData.filter(t => t.status === 'upcoming').length;
                const completed = tournamentsData.filter(t => t.status === 'completed').length;
                
                const totalParticipants = tournamentsData.reduce((sum, t) => sum + (t.current_participants || 0), 0);
                const totalPrize = tournamentsData.reduce((sum, t) => sum + (parseFloat(t.prize_pool) || 0), 0);
                
                setStats({
                    totalTournaments: total,
                    activeTournaments: active,
                    upcomingTournaments: upcoming,
                    completedTournaments: completed,
                    totalParticipants,
                    totalPrizePool: totalPrize.toFixed(4),
                    avgParticipants: total > 0 ? Math.round(totalParticipants / total) : 0,
                    avgPrizePool: total > 0 ? (totalPrize / total).toFixed(4) : 0
                });
                
                // Generate participation trend data (mock for demo)
                const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
                setParticipationData(months.map((month, idx) => ({
                    month,
                    participants: Math.floor(totalParticipants * (0.5 + idx * 0.1) / 6) || Math.floor(Math.random() * 50 + 10),
                    tournaments: Math.floor(total * (0.5 + idx * 0.1) / 6) || Math.floor(Math.random() * 5 + 1)
                })));
                
                // Prize distribution by status
                setPrizeDistribution([
                    { name: 'Active', value: active || 1, color: '#00FF66' },
                    { name: 'Upcoming', value: upcoming || 1, color: '#FFD700' },
                    { name: 'Completed', value: completed || 1, color: '#9B6FE8' }
                ]);
                
                // Tournament types breakdown
                const typeCount = {};
                tournamentsData.forEach(t => {
                    const type = t.tournament_type || 'Standard';
                    typeCount[type] = (typeCount[type] || 0) + 1;
                });
                setTournamentTypes(Object.entries(typeCount).map(([name, value]) => ({ name, value })));
                
                // Recent winners (mock - would come from completed tournaments)
                const completedTournaments = tournamentsData.filter(t => t.status === 'completed');
                const winners = completedTournaments.slice(0, 5).map((t, idx) => ({
                    address: t.winners?.[0] || `0x${Math.random().toString(16).slice(2, 42)}`,
                    tournament_name: t.name,
                    prize: (parseFloat(t.prize_pool) * 0.5).toFixed(4) || '0.01',
                    place: 1,
                    date: new Date(t.end_date).toLocaleDateString()
                }));
                setRecentWinners(winners);
            }
        } catch (error) {
            console.error('Failed to fetch analytics:', error);
        } finally {
            setLoading(false);
        }
    };
    
    if (loading) {
        return (
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    {[1, 2, 3, 4].map(i => (
                        <Skeleton key={i} className="h-28 bg-white/5" />
                    ))}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Skeleton className="h-80 bg-white/5" />
                    <Skeleton className="h-80 bg-white/5" />
                </div>
            </div>
        );
    }
    
    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="tournament-analytics-page">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#9B6FE8]/20 to-[#FFD700]/20 flex items-center justify-center">
                    <BarChart3 className="w-7 h-7 text-[#9B6FE8]" />
                </div>
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-white">Tournament Analytics</h1>
                    <p className="text-white/50 text-sm">Platform-wide tournament statistics and insights</p>
                </div>
            </div>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <StatCard 
                    title="Total Tournaments" 
                    value={stats.totalTournaments}
                    icon={Trophy}
                    color="text-[#FFD700]"
                />
                <StatCard 
                    title="Active Now" 
                    value={stats.activeTournaments}
                    icon={Zap}
                    color="text-[#00FF66]"
                />
                <StatCard 
                    title="Total Participants" 
                    value={stats.totalParticipants}
                    icon={Users}
                    color="text-[#9B6FE8]"
                />
                <StatCard 
                    title="Total Prize Pool" 
                    value={stats.totalPrizePool}
                    subValue="ETH"
                    icon={Coins}
                    color="text-[#FFD700]"
                />
            </div>
            
            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Participation Trend */}
                <Card className="bg-white/[0.02] border-white/5">
                    <CardHeader>
                        <CardTitle className="text-lg text-white flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-[#00FF66]" />
                            Participation Trend
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={250}>
                            <LineChart data={participationData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                                <XAxis dataKey="month" stroke="#ffffff50" fontSize={12} />
                                <YAxis stroke="#ffffff50" fontSize={12} />
                                <Tooltip 
                                    contentStyle={{ 
                                        backgroundColor: '#1A0B2E', 
                                        border: '1px solid #ffffff20',
                                        borderRadius: '8px'
                                    }}
                                    labelStyle={{ color: '#fff' }}
                                />
                                <Line 
                                    type="monotone" 
                                    dataKey="participants" 
                                    stroke="#00FF66" 
                                    strokeWidth={2}
                                    dot={{ fill: '#00FF66', strokeWidth: 2 }}
                                />
                                <Line 
                                    type="monotone" 
                                    dataKey="tournaments" 
                                    stroke="#9B6FE8" 
                                    strokeWidth={2}
                                    dot={{ fill: '#9B6FE8', strokeWidth: 2 }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                        <div className="flex items-center justify-center gap-6 mt-4">
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-[#00FF66]" />
                                <span className="text-white/60 text-sm">Participants</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-[#9B6FE8]" />
                                <span className="text-white/60 text-sm">Tournaments</span>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                {/* Tournament Status Distribution */}
                <Card className="bg-white/[0.02] border-white/5">
                    <CardHeader>
                        <CardTitle className="text-lg text-white flex items-center gap-2">
                            <Target className="w-5 h-5 text-[#FFD700]" />
                            Tournament Status
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                                <Pie
                                    data={prizeDistribution}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={100}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {prizeDistribution.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip 
                                    contentStyle={{ 
                                        backgroundColor: '#1A0B2E', 
                                        border: '1px solid #ffffff20',
                                        borderRadius: '8px'
                                    }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="flex items-center justify-center gap-4 mt-4">
                            {prizeDistribution.map((item, idx) => (
                                <div key={idx} className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                                    <span className="text-white/60 text-sm">{item.name} ({item.value})</span>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>
            
            {/* Bottom Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Recent Winners */}
                <div className="lg:col-span-2">
                    <Card className="bg-white/[0.02] border-white/5 h-full">
                        <CardHeader>
                            <CardTitle className="text-lg text-white flex items-center gap-2">
                                <Award className="w-5 h-5 text-[#FFD700]" />
                                Recent Winners
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <RecentWinners winners={recentWinners} />
                        </CardContent>
                    </Card>
                </div>
                
                {/* Quick Stats */}
                <Card className="bg-white/[0.02] border-white/5">
                    <CardHeader>
                        <CardTitle className="text-lg text-white flex items-center gap-2">
                            <Medal className="w-5 h-5 text-[#9B6FE8]" />
                            Averages
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="p-4 rounded-lg bg-white/[0.03]">
                            <div className="text-white/50 text-sm mb-1">Avg. Participants</div>
                            <div className="text-2xl font-bold text-white">{stats.avgParticipants}</div>
                            <div className="text-white/40 text-xs">per tournament</div>
                        </div>
                        <div className="p-4 rounded-lg bg-white/[0.03]">
                            <div className="text-white/50 text-sm mb-1">Avg. Prize Pool</div>
                            <div className="text-2xl font-bold text-[#FFD700]">{stats.avgPrizePool}</div>
                            <div className="text-white/40 text-xs">ETH per tournament</div>
                        </div>
                        <div className="p-4 rounded-lg bg-white/[0.03]">
                            <div className="text-white/50 text-sm mb-1">Upcoming</div>
                            <div className="text-2xl font-bold text-[#00FF66]">{stats.upcomingTournaments || 0}</div>
                            <div className="text-white/40 text-xs">tournaments scheduled</div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
